import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Avatar,
    Typography,
    notification,
    Image,
    Switch,
    Pagination,
    Input,
    Tag
} from "antd";
import apiClient from "../../services/api";
import { Link } from 'react-router-dom';
import moment from "moment";
import { skeletonData } from '../../components/skeletons/Users';
import femaleImageBlank from '../../assets/images/femaleAvatar.jpg'
import maleImageBlank from '../../assets/images/maleAvatar.jpg'
import { Select } from "antd";
import { handleApiError, handleApiSuccess, handleApiInfo } from "../../utils/errorHandler";
import logger from "../../utils/logger";

const { Title } = Typography;
const { Search } = Input;

// table code start
function Users() {
    const [userList, setUserList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        getUserList();
    }, [searchTerm, currentPage, pageSize]); // Update data when page or page size changes

    const columns = [
        {
            title: "#",
            dataIndex: "serial",
            key: "serial"
        },
        {
            title: "Photo",
            dataIndex: "photo",
            key: "photo",
            width: "10%",
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            width: "32%",
        },
        {
            title: "Joining Date",
            dataIndex: "joiningDate",
            key: "joiningDate",
            align: 'center'
        },
        {
            title: "Plan",
            key: "plan",
            dataIndex: "plan",
        },
        {
            title: "Status",
            key: "status",
            dataIndex: "status",
            align: 'center'
        },
        {
            title: "Block Status",
            key: "blockStatus",
            dataIndex: "blockStatus",
            align: 'center'
        },
        {
            title: "Actions",
            key: "actions",
            dataIndex: "actions",
            align: 'center'
        },
    ];

    async function getUserList(pageNo) {
        try {
            setLoading(true);

            const params = {
                page: pageNo || currentPage,
                items: pageSize,
            };

            // Check if search is not null or empty, then add it to params
            if (searchTerm && searchTerm.trim() !== '') {
                params.q = searchTerm;
                params.fields = 'firstName,lastName,email';
            }

            const response = await apiClient.get('/api/user/list', { params });
            if (response.status === 200) {
                setUserList(response.data.result);
                setTotalUsers(response.data.pagination.count);
            } else {
                setUserList([]);
                setTotalUsers(0);
            }
        } catch (error) {
            handleApiError(error);
            logger.error("Error fetching user list:", error);
            setUserList([]);
            setTotalUsers(0);
        } finally {
            setLoading(false);
        }
    }

    const handlePageChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleVerificationChange = async (userId, value) => {
        try {
            notification.destroy();
            const response = await apiClient.get(`/api/user/profile-verified/${userId}?verified=${value}`);
            if (response.data.success) {
                handleApiSuccess(response.data.message);
                getUserList();
            } else {
                handleApiInfo(response.data.message);
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
        }
    };

    async function handleBlockUser(userId, blockValue) {
        try {
            notification.destroy();
            const response = await apiClient.patch(`/api/admin/status/${userId}`, { isBlocked: blockValue });
            if (response.data.success) {
                handleApiSuccess(response.data.message);
                getUserList();
            } else {
                handleApiError(new Error(response.data.message));
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
        }
    }

    async function handleStatusChange(userId, statusValue) {
        try {
            notification.destroy();
            const response = await apiClient.patch(`/api/admin/status/${userId}`, { status: statusValue });
            if (response.data.success) {
                handleApiSuccess(response.data.message);
                getUserList();
            } else {
                handleApiError(new Error(response.data.message));
            }
        } catch (error) {
            handleApiError(error);
            logger.error("API error:", error);
        }
    }

    const handleSearch = (value) => {
        if (value.length >= 3 || value.length === 0) {
            setSearchTerm(value);
            setCurrentPage(1);
        }
    };

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="user-search-box criclebox tablespace mb-24"
                            title="Users"
                            extra={<Search placeholder="Search by name or email" onSearch={handleSearch} allowClear />}
                        >
                            {/* Wrap only the table inside the scrollable div */}
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={loading ? skeletonData : userList.map((user, index) => ({
                                        serial: (currentPage - 1) * pageSize + index + 1,
                                        photo: (<Image
                                            width={40}
                                            height={40}
                                            style={{ objectFit: 'cover', borderRadius: '6px' }}
                                            src={
                                                user.photo
                                                    ? `${user.photo}`
                                                    : user.gender === 'female'
                                                        ? femaleImageBlank
                                                        : maleImageBlank
                                            }
                                        />),
                                        name: (
                                            <Link to={`/users/${user._id}`}>
                                                <div className="avatar-info">
                                                    <Title level={5}>
                                                        {`${user.firstName || ''} ${user.lastName || ''}`}
                                                    </Title>
                                                    <p>{user.email}</p>
                                                </div>
                                            </Link>
                                        ),
                                        joiningDate: <p>{moment(user.createdAt).format("MMMM DD, YYYY")}</p>,
                                        verifyImage: user.verifiedImage ? <Image src={user.verifiedImage} /> : '-',
                                        status: (
                                            <Tag color={user.enabled ? 'success' : 'error'}>
                                                {user.enabled ? 'Active' : 'Inactive'}
                                            </Tag>
                                        ),
                                        blockStatus: (
                                            <Tag color={user.isBlocked ? 'error' : 'success'}>
                                                {user.isBlocked ? 'Blocked' : 'Unblocked'}
                                            </Tag>
                                        ),
                                        plan: (
                                            <span>
                                                {user?.planId?.name}
                                            </span>
                                        ),
                                        actions: (
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                {user.enabled ? (
                                                    <Button type="ghost" onClick={() => handleStatusChange(user._id, false)}>
                                                        Deactivate
                                                    </Button>
                                                ) : (
                                                    <Button type="dashed" onClick={() => handleStatusChange(user._id, true)}>
                                                        Activate
                                                    </Button>
                                                )}
                                                {!user.isBlocked ? (
                                                    <Button type="danger" onClick={() => handleBlockUser(user._id, true)}>
                                                        Block
                                                    </Button>
                                                ) : (
                                                    <Button type="primary" onClick={() => handleBlockUser(user._id, false)}>
                                                        Unblock
                                                    </Button>
                                                )}
                                            </div>
                                        )
                                    }))}
                                    pagination={false} // Disable default pagination inside the table
                                />
                            </div>

                            {/* Keep pagination outside the scrollable div */}
                            <div className="pagination-container">
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={totalUsers}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}

export default Users;
